import type { ListedApplicationType } from "@/domain/applications";
import type { HouseholdType } from "@/domain/household";
import type {
  RedeterminationStatusType,
  RedeterminationType,
} from "@/domain/redeterminations";
import type { WorkRequirementType } from "@/domain/workRequirements";

/**
 * Derives the patient dashboard "Next steps" feed from the three independent
 * status dimensions (eligibility, applications, renewals). The 6×9 prototype
 * matrix is the cartesian product of these axes — this pure function consumes
 * them independently so every combination is covered by construction rather
 * than by hand-coded branches.
 *
 * Keep this module free of React/React Query/runtime lib imports so it stays
 * trivially unit-testable across all combinations.
 */

export type EligibilityCardStateType = "NOT_STARTED" | "IN_PROGRESS";
export type ApplicationCardStateType = "READY" | "IN_PROGRESS";
export type WorkRequirementCardStateType =
  | "START"
  | "CONTINUE"
  | "UPLOAD_DOCUMENTS";

export type DashboardCardType =
  | { kind: "ELIGIBILITY"; state: EligibilityCardStateType }
  | { kind: "APPLICATION_SUBMITTED"; applications: ListedApplicationType[] }
  | {
      kind: "APPLICATION";
      state: ApplicationCardStateType;
      hasSubmittedApplications: boolean;
    }
  | {
      kind: "RENEWAL_GROUP";
      id: string;
      status: RedeterminationStatusType;
      items: RedeterminationType[];
      isDismissible: boolean;
    }
  | {
      kind: "WORK_REQUIREMENT";
      id: string;
      state: WorkRequirementCardStateType;
      workRequirement: WorkRequirementType;
    };

export type DashboardDraftCountsType = {
  medicaid: number;
  medicare: number;
  marketplace: number;
};

export type DashboardInputsType = {
  household?: HouseholdType;
  submittedApplications?: ListedApplicationType[];
  draftCounts: DashboardDraftCountsType;
  redeterminations?: RedeterminationType[];
  workRequirements?: WorkRequirementType[];
  dismissedCardIds: string[];
  submissionConfirmationApplicationIds: string[];
};

export const getSubmissionConfirmationDismissalId = (
  applicationId: string,
): string => `APPLICATION_SUBMITTED:${applicationId}`;

// A renewal card's identity is its status + the exact set of renewals it
// covers, so a dismissed card correctly reappears when a renewal advances to a
// new status (or a new renewal joins the group).
const renewalGroupId = (
  status: RedeterminationStatusType,
  items: RedeterminationType[],
): string =>
  `RENEWAL_GROUP:${status}:${items
    .map(item => item.id)
    .sort()
    .join(",")}`;

type BuildRenewalGroupCardParamsType = {
  status: RedeterminationStatusType;
  redeterminations: RedeterminationType[];
  dismissedIds: Set<string>;
};

const buildRenewalGroupCard = ({
  status,
  redeterminations,
  dismissedIds,
}: BuildRenewalGroupCardParamsType): DashboardCardType | null => {
  const items = redeterminations.filter(
    redetermination => redetermination.status === status,
  );

  if (items.length === 0) {
    return null;
  }

  // Only "waiting for a call" (CALL_BACK) cards are dismissible in the design.
  const isDismissible = status === "CALL_BACK";
  const id = renewalGroupId(status, items);

  if (isDismissible && dismissedIds.has(id)) {
    return null;
  }

  return { kind: "RENEWAL_GROUP", id, status, items, isDismissible };
};

// A completed screening only needs a card while proof can be uploaded now: an
// open month without proof or a missing exemption proof. Locked future months
// don't warrant one.
const getWorkRequirementCardState = (
  workRequirement: WorkRequirementType,
): WorkRequirementCardStateType | null => {
  switch (workRequirement.screeningStatus) {
    case "NOT_STARTED":
      return "START";
    case "IN_PROGRESS":
      return "CONTINUE";
    case "COMPLETED":
      return workRequirement.progress === "ACTION_NEEDED"
        ? "UPLOAD_DOCUMENTS"
        : null;
  }
};

export const buildDashboardCards = (
  inputs: DashboardInputsType,
): DashboardCardType[] => {
  const {
    household,
    submittedApplications,
    draftCounts,
    redeterminations = [],
    workRequirements = [],
    dismissedCardIds,
    submissionConfirmationApplicationIds,
  } = inputs;

  const dismissedIds = new Set(dismissedCardIds);
  const cards: DashboardCardType[] = [];

  const isEligibilityFinished = household?.eligibilityLastStep === "FINISHED";
  const hasSubmittedApplications = Boolean(submittedApplications?.length);
  const hasDraftApplications =
    draftCounts.medicaid + draftCounts.medicare + draftCounts.marketplace > 0;

  // Priority is encoded by push order (top → bottom of the feed):
  //   0. Submission confirmation             — feedback for a submit just made
  //   1. Renewal "start" (NOT_STARTED)      — actionable, above the journey
  //   2. Work requirement (start/continue/upload) — deadline-driven, actionable
  //   3. Renewal "form arriving" (PENDING_FORM)
  //   4. Eligibility check                  — only while eligibility unfinished
  //   5. Application CTA                     — only once eligibility finished
  //   6. Renewal "waiting for a call" (CALL_BACK) — low urgency, dismissible

  // Confirmation covers only submissions made on this device. Intersecting
  // those ids with the server's submitted applications drops stale ids and
  // supplies the programs/applicants the copy names; shared dashboard dismissal
  // ids suppress confirmations the patient has closed.
  const confirmationIds = new Set(submissionConfirmationApplicationIds);
  const justSubmittedApplications = (submittedApplications ?? []).filter(
    application =>
      confirmationIds.has(application.id) &&
      !dismissedIds.has(getSubmissionConfirmationDismissalId(application.id)),
  );

  // The copy names who was applied for, so a submission left without any
  // applicant name is skipped rather than rendered as a half-sentence.
  const canNameApplicants = justSubmittedApplications.some(
    application => application.applicants.length > 0,
  );

  if (canNameApplicants) {
    cards.push({
      kind: "APPLICATION_SUBMITTED",
      applications: justSubmittedApplications,
    });
  }

  // Each "to start" renewal gets its own dedicated card (not grouped/collapsed),
  // so multiple renewals-to-start render as separate top-level cards.
  for (const renewal of redeterminations.filter(
    redetermination => redetermination.status === "NOT_STARTED",
  )) {
    cards.push({
      kind: "RENEWAL_GROUP",
      id: renewalGroupId("NOT_STARTED", [renewal]),
      status: "NOT_STARTED",
      items: [renewal],
      isDismissible: false,
    });
  }

  for (const workRequirement of workRequirements) {
    const state = getWorkRequirementCardState(workRequirement);
    if (state) {
      cards.push({
        kind: "WORK_REQUIREMENT",
        id: `WORK_REQUIREMENT:${workRequirement.id}`,
        state,
        workRequirement,
      });
    }
  }

  const pendingFormRenewal = buildRenewalGroupCard({
    status: "PENDING_FORM",
    redeterminations,
    dismissedIds,
  });
  if (pendingFormRenewal) {
    cards.push(pendingFormRenewal);
  }

  if (!isEligibilityFinished) {
    cards.push({
      kind: "ELIGIBILITY",
      state: household?.eligibilityLastStep ? "IN_PROGRESS" : "NOT_STARTED",
    });
  } else if (hasDraftApplications) {
    cards.push({
      kind: "APPLICATION",
      state: "IN_PROGRESS",
      hasSubmittedApplications,
    });
  } else if (!hasSubmittedApplications) {
    cards.push({
      kind: "APPLICATION",
      state: "READY",
      hasSubmittedApplications: false,
    });
  }

  const callBackRenewal = buildRenewalGroupCard({
    status: "CALL_BACK",
    redeterminations,
    dismissedIds,
  });
  if (callBackRenewal) {
    cards.push(callBackRenewal);
  }

  return cards;
};

// Persistent sections (rendered below "Next steps") derive from the same
// inputs so a DEV scenario override drives the whole screen consistently.
export const shouldShowApplicationsSection = (
  inputs: DashboardInputsType,
): boolean => Boolean(inputs.submittedApplications?.length);

// The "Renewals" catalog lists renewals that are past NOT_STARTED. A
// NOT_STARTED renewal surfaces only as the "Start Renewal" prompt in Next
// steps; once it advances (form/call) or completes it belongs in the catalog.
export const filterRenewalsForCatalog = (
  redeterminations: RedeterminationType[],
): RedeterminationType[] =>
  redeterminations.filter(
    redetermination => redetermination.status !== "NOT_STARTED",
  );

export const getRenewalsCatalog = (
  inputs: DashboardInputsType,
): RedeterminationType[] =>
  filterRenewalsForCatalog(inputs.redeterminations ?? []);

export const shouldShowRenewalsSection = (
  inputs: DashboardInputsType,
): boolean => getRenewalsCatalog(inputs).length > 0;

// The "Work requirements" section appears once a household has been screened
// at least once, and stays through a recheck in progress.
export const shouldShowWorkRequirementsSection = (
  inputs: DashboardInputsType,
): boolean =>
  (inputs.workRequirements ?? []).some(
    workRequirement => workRequirement.screenedAt !== null,
  );
